'use client';

import type { Project, ProjectContentBlock } from '@/content/projects/types';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';
import ProjectToolEmbed from './ProjectToolEmbed';

interface ProjectArticleProps {
  project: Project;
  currentLanguage: 'en' | 'de';
  prevProject: Project | null;
  nextProject: Project | null;
}

export default function ProjectArticle({
  project,
  currentLanguage,
  prevProject,
  nextProject,
}: ProjectArticleProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return currentLanguage === 'en' ? date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : date.toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const translations = {
    en: {
      by: 'by',
      sources: 'Sources & References',
      backToProjects: '← Back to Projects',
      previous: '← Previous',
      next: 'Next →',
      newer: 'Newer',
      older: 'Older',
    },
    de: {
      by: 'von',
      sources: 'Quellen & Referenzen',
      backToProjects: '← Zurück zu Projekten',
      previous: '← Vorheriges',
      next: 'Nächstes →',
      newer: 'Neuer',
      older: 'Älter',
    },
  };

  const renderContentBlock = (block: ProjectContentBlock, index: number) => {
    switch (block.type) {
      case 'p':
        return (
          <p key={index} className="text-foreground-light text-lg leading-relaxed mb-6">
            {block.text}
          </p>
        );
      case 'h2':
        return (
          <h2
            key={index}
            className="font-heading font-bold text-3xl text-accent mt-12 mb-6"
          >
            {block.text}
          </h2>
        );
      case 'h3':
        return (
          <h3
            key={index}
            className="font-heading font-semibold text-2xl text-highlight mt-8 mb-4"
          >
            {block.text}
          </h3>
        );
      case 'ul':
        return (
          <ul key={index} className="list-disc list-inside space-y-3 mb-6 text-foreground-light">
            {block.items.map((item, i) => (
              <li key={i} className="text-lg leading-relaxed ml-4">
                {item}
              </li>
            ))}
          </ul>
        );
      case 'quote':
        return (
          <blockquote
            key={index}
            className="border-l-4 border-accent pl-6 py-4 my-8 bg-secondary/30 rounded-r-lg"
          >
            <p className="text-foreground-light text-xl italic leading-relaxed mb-2">
              "{block.text}"
            </p>
            {block.by && (
              <cite className="text-foreground-secondary text-sm not-italic">— {block.by}</cite>
            )}
          </blockquote>
        );
      case 'image':
        return (
          <figure key={index} className="my-10">
            <div className="relative w-full h-96 rounded-lg overflow-hidden">
              <AppImage
                src={block.src}
                alt={block.alt}
                fill
                sizes="(max-width: 768px) 100vw, 896px"
                loading="lazy"
                className="object-cover"
              />
            </div>
            {block.caption && (
              <figcaption className="text-foreground-muted text-sm text-center mt-3 italic">
                {block.caption}
              </figcaption>
            )}
          </figure>
        );
      case 'embed':
        if (block.component === 'ProjectToolEmbed') {
          return <ProjectToolEmbed key={index} />;
        }
        return null;
      default:
        return null;
    }
  };

  const intro = currentLanguage === 'de' && project.introDe ? project.introDe : project.intro;
  const content = currentLanguage === 'de' && project.contentDe ? project.contentDe : project.content;

  return (
    <article className="relative z-10 min-h-screen px-4 py-20">
      <div className="container mx-auto max-w-5xl">
        {/* Article Container with translucent panel */}
        <div className="bg-secondary/80 backdrop-blur-md rounded-2xl shadow-cosmic p-8 md:p-16">
          {/* Header */}
          <header className="mb-10">
            {/* Back to Projects Link */}
            <Link
              href={`/${currentLanguage}/projects-section`}
              className="inline-flex items-center text-accent hover:text-highlight transition-colors duration-fast mb-6"
            >
              {translations[currentLanguage].backToProjects}
            </Link>

            {/* Tags */}
            {project.tags && project.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {(currentLanguage === 'de' && project.tagsDe ? project.tagsDe : project.tags).map(
                  (tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-accent/20 text-accent rounded-full text-xs font-semibold uppercase tracking-wider"
                    >
                      {tag}
                    </span>
                  )
                )}
              </div>
            )}

            {/* Title */}
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-foreground-light mb-6 leading-tight">
              {currentLanguage === 'de' && project.titleDe ? project.titleDe : project.title}
            </h1>

            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-4 text-foreground-secondary text-sm">
              <span>{formatDate(project.date)}</span>
              <span>•</span>
              <span>
                {translations[currentLanguage].by} {project.author}
              </span>
            </div>

            {/* Cover Image */}
            <div className="relative w-full h-96 rounded-lg overflow-hidden mt-8">
              <AppImage
                src={project.coverImage}
                alt={currentLanguage === 'de' && project.titleDe ? project.titleDe : project.title}
                fill
                sizes="(max-width: 768px) 100vw, 896px"
                priority
                className="object-cover"
              />
            </div>
          </header>

          {/* Intro */}
          <div className="prose prose-lg max-w-none mb-8">
            {intro.map((block, index) => renderContentBlock(block, index))}
          </div>

          {/* Main Content */}
          <div className="prose prose-lg max-w-none">
            {content.map((block, index) => renderContentBlock(block, index))}
          </div>

          {/* Sources */}
          {project.sources && project.sources.length > 0 && (
            <section className="mt-16 pt-8 border-t border-accent/20">
              <h2 className="font-heading font-bold text-2xl text-foreground-light mb-6">
                {translations[currentLanguage].sources}
              </h2>
              <ul className="space-y-3">
                {project.sources.map((source, index) => (
                  <li key={index}>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:text-highlight transition-colors duration-fast underline"
                    >
                      {source.label}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Prev/Next Navigation */}
          <nav className="mt-16 pt-8 border-t border-accent/20 flex justify-between items-center">
            <div className="flex-1">
              {prevProject ? (
                <Link
                  href={`/${currentLanguage}/projects-section/${prevProject.slug}`}
                  className="group inline-flex items-center text-accent hover:text-highlight transition-colors duration-fast"
                >
                  <svg
                    className="w-5 h-5 mr-2 transition-transform duration-fast group-hover:-translate-x-1"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M15 19l-7-7 7-7" />
                  </svg>
                  <div>
                    <div className="text-xs text-foreground-muted uppercase tracking-wider">
                      {translations[currentLanguage].newer}
                    </div>
                    <div className="font-semibold">
                      {currentLanguage === 'de' && prevProject.titleDe
                        ? prevProject.titleDe
                        : prevProject.title}
                    </div>
                  </div>
                </Link>
              ) : (
                <div className="text-foreground-muted opacity-50">
                  {translations[currentLanguage].previous}
                </div>
              )}
            </div>
            <div className="flex-1 text-right">
              {nextProject ? (
                <Link
                  href={`/${currentLanguage}/projects-section/${nextProject.slug}`}
                  className="group inline-flex items-center text-accent hover:text-highlight transition-colors duration-fast"
                >
                  <div>
                    <div className="text-xs text-foreground-muted uppercase tracking-wider">
                      {translations[currentLanguage].older}
                    </div>
                    <div className="font-semibold">
                      {currentLanguage === 'de' && nextProject.titleDe
                        ? nextProject.titleDe
                        : nextProject.title}
                    </div>
                  </div>
                  <svg
                    className="w-5 h-5 ml-2 transition-transform duration-fast group-hover:translate-x-1"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              ) : (
                <div className="text-foreground-muted opacity-50">
                  {translations[currentLanguage].next}
                </div>
              )}
            </div>
          </nav>
        </div>
      </div>
    </article>
  );
}